/**
 * QUIPWA — ErrorBoundary
 *
 * React error boundary component to catch and display rendering errors gracefully.
 * Prevents entire app crash from single component failure.
 *
 * Spec section: Phase 1B — Resiliencia de UI
 */

import { Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { ErrorFallback } from './ErrorFallback'

interface Props {
  children: ReactNode
  fallback?: (error: Error, retry: () => void) => ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  level?: 'critical' | 'component' | 'section'
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log to console and optional callback
    console.error('[ErrorBoundary] Component error caught:', error, errorInfo)

    this.setState((state) => ({
      ...state,
      errorInfo,
    }))

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // Log to external service (future)
    // await logErrorToService(error, errorInfo, this.props.level)
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry)
      }

      // Use default ErrorFallback component
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          level={this.props.level}
        />
      )
    }

    return this.props.children
  }
}
